import React, { useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import api from '../services/authService';
import Icon from './Icon';
import '../styles/form-scan.css';

/**
 * Form Scan — photograph a filled-in paper questionnaire, let Gemini read
 * which boxes are ticked and what was handwritten, review the result, and
 * collect one row per form into a CSV that opens straight in Excel.
 *
 * The model only reads (POST /api/form-extract/extract returns every question
 * with its marked options). Everything else — the review table, edits, the
 * CSV layout — is plain code here, so a model swap later touches nothing on
 * this side.
 *
 * Rows collected so far live in localStorage, so switching to another tab and
 * back does not throw away an afternoon of scanning.
 */

const ROWS_STORE = 'biomedFormScanRows';
const loadRows = () => {
  try {
    const v = JSON.parse(localStorage.getItem(ROWS_STORE) || '[]');
    return Array.isArray(v) ? v : [];
  } catch (e) { return []; }
};
const saveRows = (rows) => {
  try { localStorage.setItem(ROWS_STORE, JSON.stringify(rows)); } catch (e) { /* private mode — fine */ }
};

// One cell of the sheet: what was ticked, plus any "Others" handwriting.
const answerText = (q) => {
  const picked = (q.selected || []).join('; ');
  if (q.answer_type === 'text') return q.other_text || '';
  if (q.other_text) return picked ? `${picked} | Other: ${q.other_text}` : `Other: ${q.other_text}`;
  return picked;
};

// Wide layout: one row per form, one column per question id, in the order the
// ids were first seen. Note columns appear only for questions that have one.
const buildSheet = (rows) => {
  const ids = [];
  const seen = new Set();
  const noted = new Set();
  rows.forEach((r) => r.questions.forEach((q) => {
    if (!seen.has(q.id)) { seen.add(q.id); ids.push(q.id); }
    if (q.note) noted.add(q.id);
  }));
  const columns = ['sample_id', 'scanned_at', 'pages'];
  ids.forEach((id) => { columns.push(id); if (noted.has(id)) columns.push(`${id} (note)`); });
  const data = rows.map((r) => {
    const byId = Object.fromEntries(r.questions.map((q) => [q.id, q]));
    const out = { sample_id: r.sampleId, scanned_at: r.at, pages: r.pages };
    ids.forEach((id) => {
      const q = byId[id];
      out[id] = q ? answerText(q) : '';
      if (noted.has(id)) out[`${id} (note)`] = q ? q.note || '' : '';
    });
    return out;
  });
  return { columns, data };
};

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return iso; }
};

const FormScanPanel = () => {
  const [status, setStatus] = useState(null);      // { configured, model } | { error }
  const [files, setFiles] = useState([]);          // { file, url }
  const [sampleId, setSampleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);      // { questions, pages, model, usage }
  const [rows, setRows] = useState(loadRows);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/api/form-extract/status')
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, error: true }));
  }, []);

  // Object URLs for the thumbnails are released when the file leaves the list.
  useEffect(() => () => files.forEach((f) => URL.revokeObjectURL(f.url)), [files]);

  useEffect(() => { saveRows(rows); }, [rows]);

  const maxFiles = status?.maxFiles || 8;

  const addFiles = (list) => {
    const incoming = Array.from(list || []).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type));
    if (!incoming.length) { setError('Only JPEG, PNG or WebP images can be read.'); return; }
    setError('');
    setFiles((cur) => {
      const room = Math.max(0, maxFiles - cur.length);
      const next = incoming.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }));
      if (incoming.length > room) setError(`Only ${maxFiles} pages per form — the extra images were left out.`);
      return [...cur, ...next];
    });
  };

  const removeFile = (i) => setFiles((cur) => cur.filter((_, j) => j !== i));
  const clearFiles = () => { setFiles([]); setResult(null); setError(''); };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const scan = async () => {
    if (!files.length || busy) return;
    setBusy(true);
    setSlow(false);
    setError('');
    setResult(null);
    const slowTimer = setTimeout(() => setSlow(true), 12000);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f.file, f.file.name));
      const res = await api.post('/api/form-extract/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
    } catch (e) {
      const d = e.response?.data || {};
      let msg = d.message || 'Could not reach the scanner.';
      if (d.detail) msg += ` — ${d.detail}`;
      setError(msg);
    } finally {
      clearTimeout(slowTimer);
      setBusy(false);
      setSlow(false);
    }
  };

  // Review edits: the answer cell is free text (options joined by "; "), and
  // is split back into "selected" so the sheet stays consistent.
  const editQuestion = (i, patch) => {
    setResult((cur) => {
      if (!cur) return cur;
      const questions = cur.questions.map((q, j) => (j === i ? { ...q, ...patch } : q));
      return { ...cur, questions };
    });
  };
  const editAnswer = (i, text) => {
    const selected = text.split(';').map((s) => s.trim()).filter(Boolean);
    editQuestion(i, {
      selected,
      other_text: '',
      answer_type: selected.length > 1 ? 'multi' : selected.length === 1 ? 'single' : 'none',
      confidence: 'high',
    });
  };

  const addToSheet = () => {
    if (!result) return;
    const id = sampleId.trim() || `form-${rows.length + 1}`;
    setRows((cur) => [...cur, { sampleId: id, at: new Date().toISOString(), pages: result.pages, questions: result.questions }]);
    setSampleId('');
    setFiles([]);
    setResult(null);
  };

  const removeRow = (i) => setRows((cur) => cur.filter((_, j) => j !== i));
  const clearSheet = () => setRows([]);

  const sheet = useMemo(() => buildSheet(rows), [rows]);

  const downloadCsv = () => {
    if (!rows.length) return;
    const csv = Papa.unparse({ fields: sheet.columns, data: sheet.data.map((r) => sheet.columns.map((c) => r[c] ?? '')) });
    // The BOM is what makes Excel open Thai text correctly instead of as ???.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `form-scan-${stamp}.csv`);
  };

  const configured = !!status?.configured;
  const lowCount = result ? result.questions.filter((q) => q.confidence === 'low').length : 0;

  return (
    <div className="formscan-section-custom">
      <div className="rdb-header">
        <div className="rdb-header-main">
          <span className="rdb-microlabel">
            <span className={`rdb-led ${status === null ? 'rdb-led--busy' : configured ? '' : 'rdb-led--error'}`} aria-hidden="true" />
            {status === null ? 'Form Scan · connecting' : configured ? `Form Scan · ${status.model}` : 'Form Scan · not configured'}
          </span>
          <h2 className="rdb-title font_web">Form Scan</h2>
          <p className="rdb-title-sub">photograph a filled-in questionnaire → review the answers → collect them into a CSV for Excel</p>
        </div>
        <div className="rdb-readouts">
          <div className="rdb-readout">
            <span className="rdb-readout-k">Rows in sheet</span>
            <span className="rdb-readout-v">{rows.length}</span>
          </div>
          <div className="rdb-readout">
            <span className="rdb-readout-k">Columns</span>
            <span className="rdb-readout-v">{rows.length ? sheet.columns.length : 0}</span>
          </div>
        </div>
      </div>

      {!configured && status !== null && (
        <div className="fs-alert">
          The scanner uses the same Gemini API key as the AI Agent on the Dashboard tab. A super admin needs to add it there first.
        </div>
      )}

      <div className="rdb-console fs-console">
        {/* 1 · Upload */}
        <div className="rdb-panel">
          <div className="rdb-panel-head">
            <h4>1 · Pages of one form</h4>
            <span className="rdb-inline-readout"><b>{files.length}</b> / {maxFiles} pages</span>
          </div>
          <div className="rdb-panel-body">
            <div
              className={`fs-drop${dragOver ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
              onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={busy ? undefined : onDrop}
              onClick={() => !busy && inputRef.current && inputRef.current.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) { e.preventDefault(); inputRef.current && inputRef.current.click(); } }}
            >
              <Icon name="upload-cloud" size={26} className="fs-drop-icon" />
              <strong>Drop page images here, or click to choose</strong>
              <span>JPEG · PNG · WebP — all pages of the same respondent go in one scan</span>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {files.length > 0 && (
              <ul className="fs-thumbs" aria-label="Pages to scan">
                {files.map((f, i) => (
                  <li key={f.url} className="fs-thumb">
                    <img src={f.url} alt={`Page ${i + 1}`} />
                    <span className="fs-thumb-n">p.{i + 1}</span>
                    <button type="button" className="fs-thumb-x" aria-label={`Remove page ${i + 1}`} onClick={() => removeFile(i)} disabled={busy}>
                      <Icon name="x" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="rdb-fields fs-actions">
              <div className="rdb-field">
                <label htmlFor="fs-sample">Sample / respondent ID</label>
                <input
                  id="fs-sample"
                  className="rdb-input"
                  value={sampleId}
                  placeholder="optional — e.g. BM-2026-0142"
                  onChange={(e) => setSampleId(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="rdb-actions">
                <button type="button" className="rdb-btn rdb-btn--primary" onClick={scan} disabled={!configured || !files.length || busy}>
                  <Icon name="scan" size={14} />
                  {busy ? 'Reading…' : 'Read form'}
                </button>
                <button type="button" className="rdb-btn" onClick={clearFiles} disabled={!files.length || busy}>Clear</button>
              </div>
            </div>

            {busy && (
              <p className="fs-hint">
                <span className="rdb-led rdb-led--busy" aria-hidden="true" /> Reading {files.length} page{files.length > 1 ? 's' : ''}…
                {slow && ' still working — a multi-page form can take up to a minute.'}
              </p>
            )}
            {error && <p className="fs-error" role="alert">{error}</p>}
          </div>
        </div>

        {/* 2 · Review */}
        <div className="rdb-panel">
          <div className="rdb-panel-head">
            <h4>2 · Review what was read</h4>
            {result && (
              <div className="rdb-actions">
                <span className="rdb-inline-readout">
                  <b>{result.questions.length}</b> questions
                  {lowCount > 0 && <> · <span className="fs-low">{lowCount} to check</span></>}
                </span>
                <button type="button" className="rdb-btn rdb-btn--success" onClick={addToSheet}>
                  <Icon name="plus" size={14} /> Add to sheet
                </button>
              </div>
            )}
          </div>
          <div className="rdb-panel-body">
            {!result ? (
              <div className="rdb-state fs-state">
                <p className="rdb-state-title">Nothing read yet</p>
                <p className="rdb-state-hint">upload the pages and press Read form</p>
              </div>
            ) : result.questions.length === 0 ? (
              <div className="rdb-state fs-state">
                <p className="rdb-state-title">No questions found on these pages</p>
                <p className="rdb-state-hint">check the photo is sharp and the whole page is in frame</p>
              </div>
            ) : (
              <div className="fs-table-wrap">
                <table className="fs-table">
                  <thead>
                    <tr>
                      <th className="fs-col-id">#</th>
                      <th>Question</th>
                      <th className="fs-col-ans">Answer (edit if wrong · separate several with ;)</th>
                      <th className="fs-col-note">Note</th>
                      <th className="fs-col-conf">Read</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.questions.map((q, i) => (
                      <tr key={`${q.id}-${i}`} className={q.confidence === 'low' ? 'is-low' : q.confidence === 'medium' ? 'is-mid' : ''}>
                        <td className="fs-col-id"><code>{q.id}</code></td>
                        <td>
                          <div className="fs-q">{q.question}</div>
                          {q.options.length > 0 && (
                            <div className="fs-opts">
                              {q.options.map((o) => (
                                <button
                                  key={o}
                                  type="button"
                                  className={`fs-opt${q.selected.includes(o) ? ' is-on' : ''}`}
                                  onClick={() => {
                                    const on = q.selected.includes(o);
                                    const selected = on ? q.selected.filter((s) => s !== o) : [...q.selected, o];
                                    editQuestion(i, { selected, answer_type: selected.length > 1 ? 'multi' : selected.length === 1 ? 'single' : q.other_text ? 'text' : 'none', confidence: 'high' });
                                  }}
                                  title={q.selected.includes(o) ? 'Marked — click to unmark' : 'Not marked — click to mark'}
                                >
                                  <Icon name={q.selected.includes(o) ? 'check-square' : 'square'} size={12} />
                                  {o}
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="fs-col-ans">
                          <input
                            className="rdb-input fs-cell"
                            value={q.answer_type === 'text' ? q.other_text : q.selected.join('; ')}
                            onChange={(e) => (q.answer_type === 'text' ? editQuestion(i, { other_text: e.target.value }) : editAnswer(i, e.target.value))}
                            placeholder="—"
                          />
                          {q.answer_type !== 'text' && (
                            <input
                              className="rdb-input fs-cell fs-cell-other"
                              value={q.other_text}
                              onChange={(e) => editQuestion(i, { other_text: e.target.value })}
                              placeholder="Others / free text"
                            />
                          )}
                        </td>
                        <td className="fs-col-note">
                          <input className="rdb-input fs-cell" value={q.note} onChange={(e) => editQuestion(i, { note: e.target.value })} placeholder="—" />
                        </td>
                        <td className="fs-col-conf">
                          <span className={`fs-conf fs-conf--${q.confidence}`}>{q.confidence}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 3 · Sheet */}
        <div className="rdb-panel">
          <div className="rdb-panel-head">
            <h4>3 · Sheet — one row per form</h4>
            <div className="rdb-actions">
              <button type="button" className="rdb-btn rdb-btn--primary" onClick={downloadCsv} disabled={!rows.length}>
                <Icon name="download" size={14} /> Download CSV (Excel)
              </button>
              <button type="button" className="rdb-btn rdb-btn--danger" onClick={clearSheet} disabled={!rows.length}>Clear sheet</button>
            </div>
          </div>
          <div className="rdb-panel-body">
            {!rows.length ? (
              <div className="rdb-state fs-state">
                <p className="rdb-state-title">The sheet is empty</p>
                <p className="rdb-state-hint">each form you add becomes one row; the file keeps every question as a column</p>
              </div>
            ) : (
              <div className="fs-table-wrap">
                <table className="fs-table fs-sheet">
                  <thead>
                    <tr>
                      <th className="fs-col-x" aria-label="Remove" />
                      {sheet.columns.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.data.map((r, i) => (
                      <tr key={`${rows[i].at}-${i}`}>
                        <td className="fs-col-x">
                          <button type="button" className="fs-thumb-x fs-row-x" aria-label={`Remove row ${i + 1}`} onClick={() => removeRow(i)}>
                            <Icon name="x" size={12} />
                          </button>
                        </td>
                        {sheet.columns.map((c) => (
                          <td key={c} title={c === 'scanned_at' ? fmtWhen(r[c]) : String(r[c] ?? '')}>
                            {c === 'scanned_at' ? fmtWhen(r[c]) : String(r[c] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormScanPanel;
